import { useCallback } from 'react';

import type { GraphQLAuditItem } from '../../../domain/graphql-types';
import { useSkipAuditItem as useSkipAuditItemMutation } from '../../../hooks/useSkipAuditItem';

interface UseSkipAuditItemProps {
  selectedItem: GraphQLAuditItem | null;
  onClose: () => void;
}

/**
 * Custom hook for handling skip audit item functionality in the decision panel
 * Provides skip action and loading state management
 */
export function useSkipAuditItem({ selectedItem, onClose }: UseSkipAuditItemProps) {
  const { skipAuditItem, loading: skipLoading, errors: skipErrors } = useSkipAuditItemMutation();

  const handleSkip = useCallback(async () => {
    if (!selectedItem) return;

    try {
      const result = await skipAuditItem({
        input: {
          itemId: selectedItem.id,
        },
      });

      // If skip was successful, close the dialog
      if (result.success) {
        onClose();
      }
      // If skip failed, errors will be handled by the hook's error state
    } catch (error) {
      // Errors are handled by the hook's error state
      console.error('Skip operation failed:', error);
    }
  }, [skipAuditItem, selectedItem, onClose]);

  return {
    handleSkip,
    skipLoading,
    skipErrors,
  };
}
